import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import React, { useEffect, useRef, useState } from "react";
import { Search } from "../../shared/Search";
import { EmptyList, TFormState, TListState } from "../../shared/modal";
import { EmptyECIFilter, TECIFilter, TECI, TECIRecord, EmptyECI } from "./modal";
import { Fetch, ILazyParams } from "../../utilities/fetch";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Filter } from "./Filter";
import { format } from "date-fns";
import { RequirementInfo } from "../pipeline/RequirementInfo";
import { Form } from "./Form";
import { ECIHistory } from "./History";

type TL = TListState<TECI>;
type _TF = TFormState<TECIRecord>;
type TF = _TF & { rData?: TECI; };

export const Table = () => {
    const [table, setTable] = useState(EmptyList as TL);
    const [globalSearch, setGlobalSearch] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });
    const [filters, setFilters] = useState<TECIFilter>(EmptyECIFilter);

    const [form, setForm] = useState({ open: false, data: EmptyECI } as TF);
    const hideForm = () => setForm({ open: false, data: EmptyECI });

    const [requirementId, setRequirementId] = useState<number | null>(null);

    const [history, setHistory] = useState({ open: false, data: undefined } as { open: boolean, data?: TECI });
    const hideHistory = () => setHistory({ open: false, data: undefined });

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTable(prev => ({ ...prev, loading: true }));
        const result = await Fetch<TL>("ECI",
            {
                query: new URLSearchParams({
                    lazyParams: JSON.stringify(lazyParams),
                    filters: JSON.stringify({
                        ...filters,
                        fromDate: filters.fromDate ? format(filters.fromDate as Date, 'yyyy-MM-dd') : null,
                        toDate: filters.toDate ? format(filters.toDate as Date, 'yyyy-MM-dd') : null
                    }),
                    ...(globalSearch ? { search: globalSearch } : {})
                }).toString()
            });
        if (result) {
            setTable({ data: result.data, count: result.count, loading: false });
        }
        setTable(prev => ({ ...prev, loading: false }));
    }


    useEffect(() => {
        loadTableData();
    }, [globalSearch, lazyParams, filters]);

    const openNew = () => {
        setForm({ open: true, data: EmptyECI });
    };

    const leftToolbarTemplate = () => {
        return (<React.Fragment>
            <div className="my-2">
                <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
            </div>
        </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />
            </React.Fragment>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Manage ECI</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <Search setSearch={setGlobalSearch} />
            </span>
        </div>
    );

    return (
        <>
            <Accordion>
                <AccordionTab header="Filter">
                    <Filter filters={filters} setFilters={(filters: TECIFilter) => setFilters(filters)} loading={table.loading} />
                </AccordionTab>
            </Accordion>
            <Toolbar className="mb-1 py-0" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small"
                value={table.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={table.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                emptyMessage="No Requirement found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={table.loading}
            >
                <Column field="id" header="Req ID"
                    body={(r: TECI) => (
                        <span onClick={() => setRequirementId(r.requirementId)}
                            title="Submit Candidate"
                            className="clickable-id">
                            #{r.requirementId}
                        </span>
                    )}
                />
                <Column field="jobTitle" header="Job Title" />
                <Column field="salesManager" header="Sales Manager" />
                <Column field="recruiter" header="Recruiter" />
                <Column field="candidateName" header="Candidate" />
                {/* <Column field="skillCategory" header="Skill Category" /> */}
                <Column field="ecI_Date_Time" header="ECI Date" body={(r: TECI) => format(new Date(r.ecI_Date_Time + 'z'), "MM-dd-yyyy")} />
                <Column field="ecI_Date_Time" header="Time" body={(r: TECI) => format(new Date(r.ecI_Date_Time + 'z'), "hh:mm a")} />
                <Column field="timeZone" header="Time Zone" />
                <Column field="interviewProcess" header="Interview Process" />
                <Column field="vendorCompanyName" header="PrimeVendor" />
                <Column field="subVendorCompanyName" header="SubVendor" />
                <Column field="ecI_Status" header="Status" body={(p: TECI) => {
                    if (!p.ecI_Status_IsPositive || p.ecI_Status === "Confirmed") return <span>{p.ecI_Status}</span>
                    return <span style={{ color: 'blueviolet', cursor: 'pointer' }} onClick={() => setForm({
                        open: true, data: {
                            id: p.id,
                            ecI_Date_Time: new Date(p.ecI_Date_Time + 'z'),
                            ecI_StatusId: p.ecI_StatusId,
                            interviewProcessId: p.interviewProcessId,
                            submissionId: p.submissionId,
                            timeZoneId: p.timeZoneId,
                            client: p.client,
                            interviewrName: p.interviewrName
                        }, rData: p
                    })}>{p.ecI_Status}</span>
                }} />
                <Column field="action" header="Action" body={(r: TECI) => (
                    <Button icon="pi pi-history" className="p-button-sm p-button-text" onClick={() => setHistory({ open: true, data: r })} />
                )} />
            </DataTable>
            <RequirementInfo requirementId={requirementId} hideDialog={() => setRequirementId(null)} />
            <Form open={form.open} rData={form.rData} data={form.data} hideDialog={hideForm} reload={loadTableData} />
            <ECIHistory data={history.data} hideDialog={hideHistory} open={history.open} />
        </>
    );
}
